import {
    Button,
    Chip,
    Dropdown,
    fetchRoute,
    getCalendarFormat,
    Icon,
    Link,
    TColors,
    TDataTableColumnsData,
    Typography
} from "@cakemail-org/ui-components-v2"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { Stack, Tooltip } from "@mui/material"
import { t } from "i18next"
import moment from "moment"
import { NameTableCol } from "../../components/tables/nameTableCol"
import { affixToPercent } from "../../utils/data"
import { ERoutes } from "../../utils/url"
import { TGetListingRowsCampaign } from "./types"


export function getListingColsData({ tabFilter }: { tabFilter: string }): () => TDataTableColumnsData[] {
    const cols = [
        {
            id: "name",
            label: t("general-name"),
            sortable: true,
        },
        {
            id: "list_id",
            label: t("general-list_one"),
        }
    ]


    if (["all", "delivering"].includes(tabFilter)) {
        cols.push({
            id: "created_on",
            label: t("general-created_on"),
            sortable: true
        })
    }

    if (["delivered", "delivering", "all"].includes(tabFilter)) {
        cols.push({
            id: "scheduled_for",
            label: t("general-sent")
        })
    }

    if (["delivered", "all"].includes(tabFilter)) {
        cols.splice(1, 0, {
            id: "click_rate",
            label: t("general-click_rate")
        })
        cols.splice(2, 0, {
            id: "open_rate",
            label: t("general-open_rate")
        })
    }

    cols.push({
        id: "actions",
        label: ""
    })

    return () => cols
}


export function getListingRows({
    c,
    tabFilter,
    partialReport,
    listName,
    genericWrapper,
    handleViewReport,
    handleDuplicateCampaign,
    handleDeleteCampaign,
    handleUnscheduleCampaign,
    handlePauseCampaign,
    handleResumeCampaign,
    handleCancelCampaign,
    handleRenameCampaign,
    handleOpenCampaign
}: TGetListingRowsCampaign) {

    let subStatus: string = c.status
    let statusColor: TColors = "cloud"

    if (c.under_review) {
        subStatus = "under_review"
        statusColor = "error"
    } else if (c.over_limit) {
        subStatus = "over_limit"
        statusColor = "error"
    } else if (c.suspended) {
        subStatus = "suspended"
        statusColor = "warning"
    } else if (c.status === "delivering") statusColor = "primary"
    else if (c.status === "delivered") statusColor = "secondary"
    else if (c.status === "scheduled") statusColor = "primary"

    const duplicateAction = {
        isAction: true,
        text: t("general-duplicate"),
        postContent: <Icon name="Document18" />,
        onClick: () => { handleDuplicateCampaign(c) }
    }

    const deleteAction = {
        isAction: true,
        text: t("general-delete"),
        postContent: <Icon name="Delete18" />,
        onClick: () => { handleDeleteCampaign(c) }
    }

    const unscheduleAction = {
        isAction: true,
        text: t("general-unschedule"),
        onClick: () => { handleUnscheduleCampaign(c) }
    }

    const pauseAction = {
        isAction: true,
        text: t("general-pause"),
        onClick: () => { handlePauseCampaign(c) }
    }

    const resumeAction = {
        isAction: true,
        text: t("general-resume"),
        onClick: () => { handleResumeCampaign(c) }
    }

    const cancelAction = {
        isAction: true,
        text: t("general-cancel"),
        onClick: () => { handleCancelCampaign(c) }
    }

    const reScheduleAction = {
        isAction: true,
        text: t("general-reschedule"),
        onClick: () => { handleOpenCampaign(c) }
    }

    const renameAction = {
        isAction: true,
        text: t("general-rename"),
        onClick: () => { handleRenameCampaign(c) }
    }


    let actionsDropdownOptions: TDropMenuItem[] = []
    if (subStatus === "over_limit") actionsDropdownOptions = [reScheduleAction, unscheduleAction]
    else if (subStatus === "incomplete") actionsDropdownOptions = [renameAction, duplicateAction, deleteAction]
    else if (subStatus === "suspended") actionsDropdownOptions = [resumeAction, cancelAction]
    else if (subStatus === "scheduled") actionsDropdownOptions = [reScheduleAction, unscheduleAction]
    else if (subStatus === "delivering") actionsDropdownOptions = [pauseAction, cancelAction]
    else if (subStatus === "delivered") actionsDropdownOptions = [duplicateAction, renameAction]

    const actions = <Stack direction="row" gap={3}>
        <Button
            onClick={() => { subStatus === "delivered" ? handleViewReport(c) : handleOpenCampaign(c) }}
            size="small"
            color="body1"
            variant="outlined"
        >{t(subStatus === "delivered" ? "general-view-report" : "general-open_one")}</Button>
        <Dropdown
            size="medium"
            color="body1"
            variant="outlined"
            iconOnly
            options={actionsDropdownOptions}
        />
    </Stack>

    const liveDiff = moment().diff(
        moment(moment.unix(c.scheduled_for)),
        "hours"
    )

    return {
        name: {
            id: c.name,
            content: <NameTableCol
                name={c.name}
                imgSrc={c.thumbnail_url.replace("/44/70/", "/90/120/")}
                extra={
                    <>
                        {["all", "delivering"].includes(tabFilter) && <Tooltip open={["under_review", "over_limit"].includes(subStatus) ? undefined : false} title={t("campaign-status-tooltip-" + subStatus)}><Chip label={t("general-email-" + subStatus)} color={statusColor} /></Tooltip>}
                        {tabFilter === "delivered" && liveDiff < 48 &&
                            <Tooltip open={["under_review", "over_limit"].includes(subStatus) ? undefined : false} title={t("campaign-status-tooltip-" + subStatus)}>
                                <Chip label={`${t("general-live")}: ${48 - liveDiff} ${t("general-hour-txt_one")}`} color={statusColor} />
                            </Tooltip>}
                    </>
                }
            />
        },
        created_on: {
            id: "created_on",
            content: <Typography variant="body1L">{c.created_on !== 0 ? getCalendarFormat({ unixDate: c.created_on, showTime: true }) : ""}</Typography>
        },
        scheduled_for: {
            id: "sent_on",
            content: <Typography variant="body1L">{c.scheduled_for !== 0 ? getCalendarFormat({ unixDate: c.scheduled_for, showTime: true }) : ""}</Typography>
        },
        list_id: {
            id: "list_id",
            content: <Link href={"#" + fetchRoute({ route: ERoutes.contacts }) + "?listId=" + c.audience?.list_id}>{listName || c.audience?.list_id}</Link>
        },
        open_rate: <Typography variant="body1L">{affixToPercent(partialReport?.open_rate || 0, genericWrapper.user?.language)}</Typography>,
        click_rate: <Typography variant="body1L">{affixToPercent(partialReport?.click_rate || 0, genericWrapper.user?.language)}</Typography>,
        actions: {
            id: "actions",
            content: actions
        }
    }

}